import React from "react";

import { CloudUpload } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Progress } from "@/src/components/ui/progress";
import { cn } from "@/src/lib/utils";

import type { JSX } from "react";
import type { Accept } from "react-dropzone";

const BULK_FILE_ACCEPT: Accept = { "application/json": [".json"] };

interface BulkUploadDropzoneProps {
  isUploading: boolean;
  onFileSelected: (file: File) => void;
  progress: number;
}

function BulkUploadDropzone({
  isUploading,
  onFileSelected,
  progress,
}: BulkUploadDropzoneProps): JSX.Element {
  const { t } = useTranslation("editor-bulk");

  const { getInputProps, getRootProps, isDragAccept, isDragReject } =
    useDropzone({
      accept: BULK_FILE_ACCEPT,
      disabled: isUploading,
      maxFiles: 1,
      multiple: false,
      onDrop: (acceptedFileList: File[]): void => {
        const [file] = acceptedFileList;

        if (file) {
          onFileSelected(file);
        }
      },
      onDropRejected: (): void => {
        toast.error(t("dropzoneInvalidFile"));
      },
    });

  return (
    <div
      className={cn(
        "flex h-full flex-col items-center justify-center rounded-md border-2 border-dashed px-6.25 py-10 text-center transition-colors",
        isUploading && "border-input bg-muted cursor-not-allowed",
        !isUploading &&
          isDragAccept &&
          "border-status-active bg-status-active/10",
        !isUploading &&
          isDragReject &&
          !isDragAccept &&
          "border-destructive bg-destructive/10",
        !isUploading &&
          !isDragAccept &&
          !isDragReject &&
          "group border-input hover:border-primary hover:bg-muted cursor-pointer",
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...getRootProps({ "aria-label": t("dropzoneTitle"), role: "button" })}
    >
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <input {...getInputProps()} />

      <CloudUpload className="text-subtle group-hover:text-primary mb-2 size-12 transition-colors" />

      {isUploading ? (
        <div className="flex w-full max-w-xs flex-col items-center gap-2">
          <p className="text-sm font-medium">{t("uploading")}</p>
          <Progress className="w-full" value={progress} />
        </div>
      ) : (
        <React.Fragment>
          <p className="text-md text-foreground font-medium">
            {t("dropzoneTitle")}
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            {t("dropzoneHint")}
          </p>
        </React.Fragment>
      )}
    </div>
  );
}

export default BulkUploadDropzone;
